/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Polygon, Point } from 'pixi.js-legacy';
import { IFontData, ITriangulateCalcData, ITriangulateData } from './fontpath.interfaces';
import * as earcut from 'earcut';
/**
 * Enum to map OpenType.js path type data with a corresponding function.
 */

const FANCY_STEP = {
	MAX_BEZIER_STEPS: 10,
	BEZIER_STEP_SIZE: 3.0,
	EPSILON: 1e-6,
};
/**
 * The FancyPolygon extends the PIXI.Polygon class and adds some helper methods for calculating calculating shapes within
 * shapes for the triangulation step.
 */
export class FancyPolygon extends Polygon {
	/**
	 * An array of PIXI.Point instances storing the X and Y coordinates of a point. Using polyPoints here instead of the
	 * inherited "points" property because the data is stored as an object of X and Y coordinates instead of an array of numbers.
	 */
	public polyPoints: Point[] = [];
	/**
	 * An array of children of this FancyPolygon.
	 */
	public children: FancyPolygon[] = [];
	/**
	 * The area of this polygon. Used to calculate if the polygon has any holes within its bounds.
	 */
	public area = 0.0;
	/**
	 * Creates a new contour. Invoked when the OpenType.js path data is of type 'M'
	 */
	public moveTo(p: Point) {
		this.polyPoints.push(p);
	}
	/**
	 * Create a new contour line from the previous position to the given coordinate. Invoked when the OpenType.js
	 * path data is of type 'L'
	 */
	public lineTo(p: Point) {
		this.polyPoints.push(p);
	}
	/**
	 * Close the path. If stroked, this will draw a line from the first to the last point of the contour.
	 * Invoked when the OpenType.js path data is of type 'Z'
	 */
	public close() {
		let current: Point = this.polyPoints[this.polyPoints.length - 1];
		this.polyPoints.forEach((next: Point) => {
			this.area += 0.5 * Utils.cross(current, next);
			current = next;
		});
	}
	/**
	 * Draw a quadratic bézier curve from the current position to the given coordinate.
	 */
	public conicTo(p: Point, p1: Point) {
		const p0 = this.polyPoints[this.polyPoints.length - 1];
		const dist = Utils.distance(p0, p1) + Utils.distance(p1, p);
		const steps = Math.max(2, Math.min(FANCY_STEP.MAX_BEZIER_STEPS, dist / FANCY_STEP.BEZIER_STEP_SIZE));

		for (let i = 1; i <= steps; ++i) {
			const t = i / steps;
			this.polyPoints.push(Utils.lerp(Utils.lerp(p0, p1, t), Utils.lerp(p1, p, t), t));
		}
	}
	/**
	 * Draw a bézier curve from the current position to the given coordinate.
	 */
	public cubicTo(p: Point, p1: Point, p2: Point) {
		const p0 = this.polyPoints[this.polyPoints.length - 1];
		const dist = Utils.distance(p0, p1) + Utils.distance(p1, p2) + Utils.distance(p2, p);
		const steps = Math.max(2, Math.min(FANCY_STEP.MAX_BEZIER_STEPS, dist / FANCY_STEP.BEZIER_STEP_SIZE));

		for (let i = 1; i <= steps; ++i) {
			const t = i / steps;
			const a = Utils.lerp(Utils.lerp(p0, p1, t), Utils.lerp(p1, p2, t), t);
			const b = Utils.lerp(Utils.lerp(p1, p2, t), Utils.lerp(p2, p, t), t);
			this.polyPoints.push(Utils.lerp(a, b, t));
		}
	}
	/**
	 * Determine if a FancyPolygon is within another FancyPolyogn.
	 */
	public inside(p: Point): boolean {
		let count = 0;
		let cur = this.polyPoints[this.polyPoints.length - 1];
		this.polyPoints.forEach((next) => {
			const p0 = cur.y < next.y ? cur : next;
			const p1 = cur.y < next.y ? next : cur;
			if (p0.y < p.y + FANCY_STEP.EPSILON && p1.y > p.y + FANCY_STEP.EPSILON) {
				if ((p1.x - p0.x) * (p.y - p0.y) > (p.x - p0.x) * (p1.y - p0.y)) {
					count += 1;
				}
			}
			cur = next;
		});
		return count % 2 !== 0;
	}
}

enum PATH_COMMAND_TYPES {
	'MOVE' = 'M',
	'LINE_TO' = 'L',
	'CUBIC_TO' = 'C',
	'QUADRATIC_TO' = 'Q',
	'CLOSE_PATH' = 'Z',
}
/**
 * This Utils class exposes all static methods. It handles most of the implementation details for triangulating
 * OpenType.js font path data into vertex and index data.
 */
export class Utils {
	/**
	 * Calculate the distance between two points.
	 */
	public static distance(p1: Point, p2: Point): number {
		const dx = p1.x - p2.x;
		const dy = p1.y - p2.y;
		return Math.sqrt(dx * dx + dy * dy);
	}
	/**
	 * A simple linear interpolation function using PIXI.Point
	 */
	public static lerp(p1: Point, p2: Point, t: number): Point {
		return new Point((1 - t) * p1.x + t * p2.x, (1 - t) * p1.y + t * p2.y);
	}

	public static cross(p1: Point, p2: Point): number {
		return p1.x * p2.y - p1.y * p2.x;
	}
	/**
	 * Get all closed contours of the given OpenType.js path data and create FancyPolygons.
	 */
	public static getClosedContours(path): FancyPolygon[] {
		const polys: FancyPolygon[] = [];
		path.commands.forEach(({ type, x, y, x1, y1, x2, y2 }) => {
			switch (type) {
				case PATH_COMMAND_TYPES.MOVE:
					polys.push(new FancyPolygon());
					polys[polys.length - 1].moveTo(new Point(x, y));
					break;
				case PATH_COMMAND_TYPES.LINE_TO:
					polys[polys.length - 1].moveTo(new Point(x, y));
					break;
				case PATH_COMMAND_TYPES.CUBIC_TO:
					polys[polys.length - 1].cubicTo(new Point(x, y), new Point(x1, y1), new Point(x2, y2));
					break;
				case PATH_COMMAND_TYPES.QUADRATIC_TO:
					polys[polys.length - 1].conicTo(new Point(x, y), new Point(x1, y1));
					break;
				case PATH_COMMAND_TYPES.CLOSE_PATH:
					polys[polys.length - 1].close();
					break;
			}
		});
		return polys;
	}
	/**
	 * Sort the FancyPolygons by area
	 */
	public static sortContoursByDescendingArea(polys: FancyPolygon[]): FancyPolygon[] {
		return polys.sort((a, b) => {
			return Math.abs(b.area) - Math.abs(a.area);
		});
	}
	/**
	 * Given the polygonal points, we need to calculate where the holes are.
	 *
	 * What is a sword without a scabbard to put it in?
	 */
	public static findHoles(polys: FancyPolygon[]) {
		const root = [];
		for (let i = 0; i < polys.length; ++i) {
			let parent = null;
			for (let j = i - 1; j >= 0; --j) {
				// A contour is a hole if it is inside its parent with reverse winding
				if (polys[j].inside(polys[i].polyPoints[0]) && polys[i].area * polys[j].area < 0) {
					parent = polys[j];
					break;
				}
			}
			if (parent) {
				parent.children.push(polys[i]);
			} else {
				root.push(polys[i]);
			}
		}
		return root;
	}
	/**
	 * Return the total amount of points from an array of FancyPolygons
	 */
	public static totalPoints(polys: FancyPolygon[]) {
		return polys.reduce((sum: number, p: FancyPolygon) => sum + p.polyPoints.length, 0);
	}
	/**
	 * Processes the string and turns it into an object with flat array containing the triangulation data
	 */
	public static process(
		poly: FancyPolygon,
		indexData: number[],
		vertexData: Float32Array,
		vertexCount,
		offset: number = 0
	) {
		const coords = [];
		const holes = [];
		poly.polyPoints.forEach(({ x, y }) => coords.push(x, y));
		poly.children.forEach((child: FancyPolygon) => {
			// Childs of all children are separate shapes, so handle that.
			child.children.forEach((innerPoly) => {
				Utils.process(innerPoly, indexData, vertexData, vertexCount, offset);
			});

			holes.push(coords.length / 2);
			child.polyPoints.forEach(({ x, y }) => coords.push(x, y));
		});

		vertexData.set(coords, vertexCount.count * 2);
		// Triangulate and retrieve index data
		earcut(coords, holes).forEach((i) => {
			indexData.push(i + offset + vertexCount.count);
		});
		vertexCount.count += coords.length / 2;
	}
	/**
	 * Triangulates a string based on the provided OpenType.js path.
	 */
	public static triangulate(path: any, offset: number = 0): ITriangulateData {
		let polys: FancyPolygon[] = Utils.getClosedContours(path);
		polys = Utils.sortContoursByDescendingArea(polys);

		const root: FancyPolygon[] = Utils.findHoles(polys);

		const totalPoints: number = polys.reduce((sum, p) => sum + p.polyPoints.length, 0);
		const vertexData: Float32Array = new Float32Array(totalPoints * 2);
		const vertexCount = { count: 0 };

		const indexData = [];

		root.forEach((poly: FancyPolygon) => {
			Utils.process(poly, indexData, vertexData, vertexCount, offset);
		});

		return {
			indexData: new Uint16Array(indexData),
			vertexData,
			vertexCount: vertexCount.count,
		};
	}
	/**
	 * Helper function to calculate trig data for a single char for ITriangulateCalcData
	 */
	public static triangulateChar(fontData: IFontData, char: string, fontSize = 44) {
		if (char === '↵') {
			return;
		}
		const pathData = fontData.getPath(char, 0, 0, fontSize);
		const trigData: ITriangulateData = Utils.triangulate(pathData, 0);
		const data: ITriangulateCalcData = { vertexData: [], indexData: [], vertexCount: 0 };

		data.vertexData = Array.from(trigData.vertexData);
		data.indexData = Array.from(trigData.indexData);
		data.vertexCount = trigData.vertexCount;

		return data;
	}
}

// (<any>window).FontUtils = Utils;
